/**
 * @grow/api-client
 *
 * Re-exports the openapi-typescript-generated `paths` and `components`
 * from the FastAPI OpenAPI spec, plus a typed fetch helper.
 *
 * Regenerate after backend schema changes: `pnpm api:generate`.
 */

import type { components, paths } from './schema.generated.js';

export type { components, paths } from './schema.generated.js';

// Public survey (Slice 1)
export type SessionState = components['schemas']['SessionStateOut'];
export type AnswerIn = components['schemas']['AnswerIn'];
export type QuestionOut = components['schemas']['QuestionOut'];
export type AnswerOut = components['schemas']['AnswerOut'];
export type CustomerOut = components['schemas']['CustomerOut'];
export type SessionStatus = components['schemas']['SessionStatus'];
export type Classification = components['schemas']['Classification'];
export type QuestionType = components['schemas']['QuestionType'];
export type SurveyType = components['schemas']['SurveyType'];

// Conversational survey (Phase 3)
export type SessionChannel = components['schemas']['SessionChannel'];
export type ChatState = components['schemas']['ChatStateOut'];
export type ChatTurn = components['schemas']['TurnOut'];
export type TurnRole = components['schemas']['TurnRole'];

// Admin (Slice 2)
export type LoginRequest = components['schemas']['LoginRequest'];
export type LoginResponse = components['schemas']['LoginResponse'];
/** Entra ID token (obtained by MSAL) exchanged for a Grow session token. */
export interface SsoCallbackRequest {
  id_token: string;
}
/**
 * Staff RBAC (2026-08-10): ADMIN (full access + staff management), MANAGER
 * (full survey read/write, no staff management), USER (read-only, scoped to
 * one portfolio via pm_id). VIEWER is a legacy role no longer provisioned.
 * Hand-typed until the next `pnpm api:generate` picks up the new fields.
 */
export type AdminRole = 'ADMIN' | 'MANAGER' | 'USER' | 'VIEWER';
export interface MeOut {
  id: string;
  email: string;
  name: string;
  role: AdminRole;
  auth_method: 'ENTRA_SSO' | 'PASSWORD';
  last_login_at: string | null;
  /** The portfolio a USER is scoped to — null for ADMIN/MANAGER/VIEWER. */
  pm_id: string | null;
  pm_name: string | null;
}
export type SessionListItem = components['schemas']['SessionListItem'];
/**
 * Aggregate session counts for the responses-screen filter tabs.
 *
 * `all` is every session, answered or not (i.e. surveys sent) — the Responded
 * tab reads `responded`, not `all`. `responded + pending === all`.
 */
export interface SessionCounts {
  all: number;
  /** Answered at least one question — the Responded tab. */
  responded: number;
  /** Nobody has answered yet, opened-but-abandoned included — the Pending tab. */
  pending: number;
  promoter: number;
  passive: number;
  detractor: number;
}
export type SessionDetailOut = components['schemas']['SessionDetailOut'];
export type ChatTurnItem = components['schemas']['ChatTurnItem'];
/**
 * Admin AI playground — mint a throwaway flagged test session (non-prod only).
 * Hand-typed until the next `pnpm api:generate` picks up the new endpoint.
 */
export interface PlaygroundSessionIn {
  channel: SessionChannel;
  /** Required for SMS_CHAT: the handset to text. Must already be a flagged test contact. */
  phone_e164?: string;
}
export interface PlaygroundSession {
  session_id: string;
  token: string;
  link: string;
  channel: SessionChannel;
}
export type DetractorItem = components['schemas']['DetractorItem'];
export type DashboardStats = components['schemas']['DashboardStatsOut'];
export type AnswerWithQuestion = components['schemas']['AnswerWithQuestion'];

// Staff/Users management — the Admin-only Team screen (staff-RBAC, 2026-08-10).
// Hand-typed until the next `pnpm api:generate` picks up the new endpoints.
export type StaffInviteStatus = 'INVITED' | 'ACTIVE';

export interface StaffMember {
  id: string;
  email: string;
  name: string;
  role: AdminRole;
  auth_method: 'ENTRA_SSO' | 'PASSWORD';
  is_active: boolean;
  pm_id: string | null;
  pm_name: string | null;
  invite_status: StaffInviteStatus;
  invited_at: string | null;
  last_login_at: string | null;
}

/** Role for a new invite — VIEWER is legacy and can't be assigned to new staff. */
export type InvitableRole = 'ADMIN' | 'MANAGER' | 'USER';

export interface StaffInvite {
  email: string;
  name: string;
  role: InvitableRole;
  /** Required when role is USER (the portfolio they're scoped to); omit otherwise. */
  pm_id?: string | null;
}

export interface StaffUpdate {
  role: InvitableRole;
  pm_id?: string | null;
  is_active: boolean;
}

// Self-service survey question editor (TRD §15)
export type QuestionAdminOut = components['schemas']['QuestionAdminOut'];
export type SurveyQuestionsOut = components['schemas']['SurveyQuestionsOut'];
export type QuestionCreate = components['schemas']['QuestionCreate'];
export type QuestionUpdate = components['schemas']['QuestionUpdate'];
export type QuestionReorder = components['schemas']['QuestionReorder'];

// Surveys list + duplication (the admin Surveys screen). A duplicate is always
// type CUSTOM — many surveys can share it, unlike the four seeded singletons.
export type SurveySummary = components['schemas']['SurveySummaryOut'];
export type SurveyUpdate = components['schemas']['SurveyUpdateIn'];

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiClientConfig {
  baseUrl: string;
  /** Optional — when set, the fetcher injects Authorization: Bearer <token>. */
  getAuthToken?: () => string | null;
  /** Optional — called when any request returns 401 (e.g. clear token + redirect). */
  onUnauthorized?: () => void;
}

export interface SessionListFilters {
  status?: SessionStatus;
  /**
   * Responses-screen tabs: `true` = answered at least one question (Responded),
   * `false` = nobody has answered yet (Pending). Not the same as `status` — a
   * session goes IN_PROGRESS the moment the link is opened, answered or not.
   */
  responded?: boolean;
  classification?: Classification;
  survey_type?: SurveyType;
  pm_id?: string;
  /** Survey history for one contact — backs the Contact detail page. */
  customer_id?: string;
  /** Survey history for one property — backs the Property detail page. */
  property_id?: string;
  q?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

export interface PropertyManagerOption {
  id: string;
  name: string;
}

// --- Reports (the /reports screen: filterable analytics + CSV export) ---
export interface ReportFilters {
  survey_id?: string;
  pm_id?: string;
  date_from?: string;
  date_to?: string;
}

export interface ReportSummary {
  sent: number;
  responded: number;
  response_rate: number;
  nps: number;
  promoter: number;
  passive: number;
  detractor: number;
  avg_total_score: number | null;
  total_score_max: number;
  scored_responses: number;
}

export interface ReportSectionRow {
  question_id: string;
  order_index: number;
  label: string;
  text: string;
  scale_max: number;
  avg_score: number | null;
  response_count: number;
}

export interface ReportPmRow {
  pm_id: string | null;
  pm_name: string;
  sent: number;
  responded: number;
  response_rate: number;
  nps: number;
  avg_total_score: number | null;
}

export interface ReportScoreBin {
  score: number;
  count: number;
  band: Classification;
}

export interface ReportCommentItem {
  session_id: string;
  customer_name: string;
  pm_name: string | null;
  answered_at: string;
  text: string;
}

/** A question that stays out of the /max total — free-text, yes/no or opted out of scoring. */
export interface ReportExtraQuestionRow {
  question_id: string;
  order_index: number;
  text: string;
  question_type: QuestionType;
  response_count: number;
  yes_count: number;
  no_count: number;
  avg_score: number | null;
  scale_max: number;
  comments: ReportCommentItem[];
}

export interface ReportOut {
  survey_id: string;
  survey_name: string;
  survey_type: SurveyType;
  question_count: number;
  total_score_max: number;
  summary: ReportSummary;
  by_section: ReportSectionRow[];
  by_pm: ReportPmRow[];
  primary_question_label: string | null;
  score_distribution: ReportScoreBin[];
  by_extra_question: ReportExtraQuestionRow[];
}

// --- Cross-survey overview (every survey type, the funnel and the trend) ---
export interface OverviewFilters {
  pm_id?: string;
  date_from?: string;
  date_to?: string;
}

export interface SurveyBreakdownRow {
  survey_id: string;
  survey_type: SurveyType;
  survey_name: string;
  is_active: boolean;
  sent: number;
  responded: number;
  response_rate: number;
  nps: number;
  promoter: number;
  passive: number;
  detractor: number;
  avg_total_score: number | null;
  total_score_max: number;
  /** total/max as a percentage — the score that compares across surveys. */
  avg_score_pct: number | null;
  scored_responses: number;
  open_detractors: number;
  last_sent_at: string | null;
}

export interface OverviewTotals {
  sent: number;
  responded: number;
  response_rate: number;
  nps: number;
  promoter: number;
  passive: number;
  detractor: number;
  avg_score_pct: number | null;
  scored_responses: number;
  open_detractors: number;
  expired: number;
  surveys_with_activity: number;
  survey_count: number;
}

export interface FunnelStageRow {
  key: string;
  label: string;
  count: number;
  pct_of_sent: number;
}

/** One period across all surveys. Monthly in `trend`, weekly in `trend_weekly`. */
export interface TrendPoint {
  /** "YYYY-MM" monthly, "YYYY-Www" weekly. */
  period: string;
  /** "Jul 2026" monthly, "11 Aug 2026" (week commencing) weekly. */
  label: string;
  sent: number;
  responded: number;
  response_rate: number;
  nps: number;
  promoter: number;
  detractor: number;
  avg_score_pct: number | null;
}

export interface ReportOverviewOut {
  generated_at: string;
  totals: OverviewTotals;
  by_survey: SurveyBreakdownRow[];
  funnel: FunnelStageRow[];
  trend: TrendPoint[];
  /** Same series bucketed by week. Both are gap-filled — callers drop empties. */
  trend_weekly: TrendPoint[];
}

export interface PortfolioManagerRow {
  portfolio_id: string;
  portfolio_name: string;
  pm_id: string | null;
  first_name: string | null;
  last_name: string | null;
  name: string | null;
  email: string | null;
  phone: string | null;
  active: boolean;
  updated_at: string;
}

export interface PortfolioManagerUpdate {
  portfolio_name: string;
  first_name: string;
  last_name?: string | null;
  email: string;
  phone?: string | null;
  active: boolean;
}

export interface SmsVariable {
  key: string;
  token: string;
  description: string;
  example: string;
}

export interface SmsTemplate {
  survey_id: string;
  survey_name: string;
  survey_type: string;
  is_active: boolean;
  template: string;
  /** Follow-up reminder body sent to non-responders. Same {{variable}} catalog. */
  reminder_template: string;
  /**
   * Default channel for a manual send of this survey: true sends it as an Aimee
   * conversation over SMS, false sends the link. A single send can override it either
   * way; automated sends follow the rollout percentage instead.
   */
  sms_chat_default: boolean;
  /** Admin-only kill switch: false means no new follow-up reminder is scheduled for
   * this survey from any send path (manual/batch send or a drip campaign). */
  reminders_enabled: boolean;
  updated_at: string;
}

export interface SmsTemplates {
  variables: SmsVariable[];
  templates: SmsTemplate[];
}

/** Payload for updating one survey's SMS templates. Omit reminder_template to leave it unchanged. */
export interface SmsTemplateUpdate {
  template: string;
  reminder_template?: string;
  /** Omit to leave the survey's channel untouched — a copy edit shouldn't switch it. */
  sms_chat_default?: boolean;
  /** Admin-only. Omit to leave the stored value untouched; the backend rejects this
   * field with a 403 unless the caller is an ADMIN. */
  reminders_enabled?: boolean;
}

export interface ReminderSettings {
  /** Business days after the initial send that a non-responder gets nudged (read-only). */
  reminder_after_days: number;
  /** Wall-clock "HH:MM" (24-hour, app timezone) the reminder goes out — admin-editable. */
  reminder_send_time_local: string;
}

/** The automated triggered-survey schedule shown on the Routine inspection / New tenancy tabs. */
export interface TriggeredAutosend {
  /** Environment kill switch. False ⇒ nothing auto-sends, whatever the event types say. */
  enabled: boolean;
  /** Daily send time, app-local "HH:MM". Admin-editable. */
  send_time: string;
  /** Event types currently automated. */
  event_types: PmEventType[];
  /** Event types an admin may automate — lease renewal is excluded by policy. */
  automatable_event_types: PmEventType[];
  timezone: string;
  business_hours_start: string;
  business_hours_end: string;
  /** App-local date of the last completed run; null before the first one. */
  last_run_date: string | null;
}

export interface TriggeredAutosendUpdate {
  send_time: string;
  event_types: PmEventType[];
}

export interface ContactCreate {
  full_name: string;
  phone_e164: string;
  email?: string | null;
  role?: CustomerRole;
  property_address?: string | null;
  suburb?: string | null;
  postcode?: string | null;
  pm_id?: string | null;
}

export type CustomerRole = 'OWNER' | 'TENANT' | 'TRADE' | 'OTHER';

export interface ContactUpdate extends ContactCreate {
  /** Omit to leave suppression as-is — sending `false` un-suppresses the contact. */
  do_not_contact?: boolean;
  /** Required when this un-suppresses a contact; kept in the audit log. */
  opt_out_reason?: string;
}

export interface OptOutUpdate {
  do_not_contact: boolean;
  /** Required when clearing an opt-out (do_not_contact: false); kept in the audit log. */
  reason?: string;
}

export interface ContactOut {
  id: string;
  full_name: string;
  /** PropertyMe preferred greeting name; SMS falls back to first name if null. */
  salutation: string | null;
  phone_e164: string;
  email: string | null;
  role: CustomerRole;
  property_address: string | null;
  suburb: string | null;
  postcode: string | null;
  pm_id: string | null;
  pm_name: string | null;
  do_not_contact: boolean;
  /** When the contact was suppressed; null while contactable. */
  opted_out_at: string | null;
  /** ADMIN | SMS_STOP | CHAT, or null when the suppression predates source tracking. */
  opt_out_source: string | null;
  /**
   * Admin who set the current opt-out. Populated by the single-contact read only —
   * list responses always carry null (resolving it per row would be an N+1).
   */
  opted_out_by_name: string | null;
  /**
   * Non-prod SMS sandbox allow-list. Outside production only a flagged contact can
   * receive a real SMS — every other send is recorded as blocked. Always false in
   * production, where nothing consults it.
   */
  is_test_contact: boolean;
  /** Set while archived: hidden from lists and never messaged, survey history kept. */
  archived_at: string | null;
  created_at: string;
}

export interface SendSurveyOut {
  session_id: string;
  survey_link: string;
  to_phone: string;
  sms_status: string;
  sms_body: string;
}

export interface BatchSendFailure {
  customer_id: string;
  error: string;
}

export interface BatchSendOut {
  sent: SendSurveyOut[];
  failed: BatchSendFailure[];
  skipped_do_not_contact: number;
  skipped_recently_surveyed: number;
  /** Owners skipped by the spacing floor or because another of their properties already sent. */
  skipped_owner_spacing: number;
  /** Owners skipped because they've already had the maximum surveys allowed in the window. */
  skipped_owner_cap?: number;
}

/** One (owner, property) survey target — the surveyable unit. */
export interface SendTarget {
  customer_id: string;
  /** The property the survey is about; null falls back to the contact's primary property. */
  property_id?: string | null;
}

/** One (owner, property) recipient row for the compose picker. */
export interface OwnershipTargetOut {
  customer_id: string;
  property_id: string | null;
  full_name: string;
  phone_e164: string;
  property_address: string | null;
  suburb: string | null;
  pm_name: string | null;
  role: string | null;
  do_not_contact: boolean;
}

/** One owner of a property (Properties page). */
export interface PropertyOwnerItem {
  customer_id: string;
  full_name: string;
  phone_e164: string;
  role: string; // PRIMARY / SECONDARY
  do_not_contact: boolean;
}

/** A property with its PM and active owner(s) — a Properties page row. */
export interface PropertyWithOwnersOut {
  property_id: string;
  address_line: string;
  suburb: string | null;
  postcode: string | null;
  pm_name: string | null;
  owners: PropertyOwnerItem[];
}

/** A PropertyMe event type that triggers a survey (Phase 2). */
export type PmEventType = 'INSPECTION' | 'NEW_TENANCY' | 'LEASE_RENEWAL';

/** An ingested PM event with its resolved owner/property — a manual-send candidate. */
export interface PmEventTarget {
  pm_event_id: string;
  event_type: PmEventType;
  event_date: string;
  survey_id: string;
  customer_id: string | null;
  full_name: string | null;
  phone_e164: string | null;
  property_id: string | null;
  property_address: string | null;
  suburb: string | null;
  pm_name: string | null;
  do_not_contact: boolean;
  /** True if the re-survey guard would block this send (same survey, same property, within the window). */
  recently_surveyed: boolean;
  /** True if the owner has already had the maximum surveys allowed in the cap window. */
  owner_cap_reached?: boolean;
  /** How many surveys the owner has been sent inside the cap window. */
  owner_surveys_in_window?: number;
  /** True if the per-owner spacing floor would block this send (owner contacted very recently). */
  owner_contacted_recently?: boolean;
  /** Set once the survey has been sent for this event. */
  processed_at: string | null;
  session_id: string | null;
}

/** A General Survey batch queued for a future send (TRD §6.1). */
export interface ScheduledBatchOut {
  id: string;
  survey_id: string;
  status: 'SCHEDULED' | 'SENT' | 'CANCELLED' | 'FAILED';
  recipient_count: number;
  scheduled_for_utc: string;
  scheduled_for_local: string;
  timezone: string;
  /** False in local/dev (no Cloud Tasks) — recorded but not enqueued. */
  cloud_task_queued: boolean;
  created_at: string;
  dispatched_at: string | null;
  sent_count: number | null;
  failed_count: number | null;
  skipped_count: number | null;
  error: string | null;
}

export interface SmsPreviewItem {
  customer_id: string;
  property_id: string | null;
  full_name: string;
  to_phone: string;
  body: string;
}

export interface SmsPreviewOut {
  previews: SmsPreviewItem[];
  skipped_do_not_contact: number;
}

/** Config for an automated daily drip of the General Survey (no manual recipients). */
export interface DripCampaignIn {
  batch_size: number;
  /** Daily fire time, "HH:MM" wall-clock in the app timezone (Australia/Brisbane). */
  send_time: string;
  /** "YYYY-MM-DD" */
  start_date: string;
  /** "YYYY-MM-DD" or null to drain the whole eligible pool. */
  end_date?: string | null;
  reminder_after_days?: number | null;
}

export interface DripCampaignOut {
  id: string;
  survey_id: string;
  status: 'ACTIVE' | 'COMPLETED' | 'CANCELLED' | 'FAILED';
  batch_size: number;
  send_time: string;
  start_date: string;
  end_date: string | null;
  reminder_after_days: number | null;
  next_run_local: string | null;
  total_sent: number;
  days_run: number;
  /** Whole days left to drain the current eligible pool at batch_size (ACTIVE only). */
  estimated_days: number;
  timezone: string;
  cloud_task_queued: boolean;
  created_at: string;
  error: string | null;
}

export interface EligibleCountOut {
  eligible: number;
}

/** One projected send day of a drip. */
export interface DripScheduleDay {
  /** "YYYY-MM-DD" (app-local). */
  date: string;
  count: number;
}

/** Projected per-day send schedule for a drip (feeds the date dropdown). */
export interface DripScheduleOut {
  timezone: string;
  days: DripScheduleDay[];
}

/** One contact projected to be sent on a drip day, with their resolved SMS. */
export interface DripRecipientOut {
  customer_id: string;
  property_id: string | null;
  full_name: string;
  salutation: string | null;
  to_phone: string;
  body: string;
}

/** Partial edit of a contact's greeting name (fix odd owner names). */
export interface GreetingUpdate {
  full_name?: string | null;
  salutation?: string | null;
}

export function createApiClient(config: ApiClientConfig) {
  const root = config.baseUrl.replace(/\/$/, '');

  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...((init.body ? { 'Content-Type': 'application/json' } : {}) as Record<string, string>),
      ...((init.headers ?? {}) as Record<string, string>),
    };
    const token = config.getAuthToken?.() ?? null;
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const res = await fetch(`${root}${path}`, { ...init, headers });

    if (res.status === 401) {
      config.onUnauthorized?.();
    }

    if (!res.ok) {
      let code = 'http_error';
      let message = res.statusText;
      try {
        const body = (await res.json()) as { error?: { code?: string; message?: string } };
        if (body.error) {
          code = body.error.code ?? code;
          message = body.error.message ?? message;
        }
      } catch {
        // body wasn't JSON — keep statusText
      }
      throw new ApiError(res.status, code, message);
    }
    if (res.status === 204) {
      return undefined as T;
    }
    return (await res.json()) as T;
  }

  function buildQuery(params: Record<string, unknown>): string {
    const u = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== '') u.set(k, String(v));
    }
    const s = u.toString();
    return s ? `?${s}` : '';
  }

  return {
    survey: {
      getSession(token: string): Promise<SessionState> {
        return request<SessionState>(`/api/v1/public/sessions/${encodeURIComponent(token)}`);
      },
      submitAnswer(token: string, payload: AnswerIn): Promise<SessionState> {
        return request<SessionState>(
          `/api/v1/public/sessions/${encodeURIComponent(token)}/answer`,
          { method: 'POST', body: JSON.stringify(payload) },
        );
      },
      /** Finalize the survey from the review screen — freezes answers + classifies. */
      submitSession(token: string): Promise<SessionState> {
        return request<SessionState>(
          `/api/v1/public/sessions/${encodeURIComponent(token)}/submit`,
          { method: 'POST' },
        );
      },
      /** Transcript + flow hints for a WEB_CHAT session (seeds the greeting on first call). */
      getChat(token: string): Promise<ChatState> {
        return request<ChatState>(`/api/v1/public/sessions/${encodeURIComponent(token)}/chat`);
      },
      /** One respondent chat message in; Aimee's reply turns out. */
      postChat(token: string, message: string): Promise<ChatState> {
        return request<ChatState>(`/api/v1/public/sessions/${encodeURIComponent(token)}/chat`, {
          method: 'POST',
          body: JSON.stringify({ message }),
        });
      },
    },

    auth: {
      login(payload: LoginRequest): Promise<LoginResponse> {
        return request<LoginResponse>('/api/v1/auth/login', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      },
      ssoCallback(payload: SsoCallbackRequest): Promise<LoginResponse> {
        return request<LoginResponse>('/api/v1/auth/sso/callback', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      },
      logout(): Promise<void> {
        return request<void>('/api/v1/auth/logout', { method: 'POST' });
      },
    },

    admin: {
      me(): Promise<MeOut> {
        return request<MeOut>('/api/v1/admin/me');
      },
      // Staff/Users management — ADMIN-only (staff-RBAC, 2026-08-10).
      listStaff(): Promise<StaffMember[]> {
        return request<StaffMember[]>('/api/v1/admin/staff');
      },
      inviteStaff(payload: StaffInvite): Promise<StaffMember> {
        return request<StaffMember>('/api/v1/admin/staff', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      },
      updateStaff(staffId: string, payload: StaffUpdate): Promise<StaffMember> {
        return request<StaffMember>(`/api/v1/admin/staff/${encodeURIComponent(staffId)}`, {
          method: 'PATCH',
          body: JSON.stringify(payload),
        });
      },
      resendStaffInvite(staffId: string): Promise<StaffMember> {
        return request<StaffMember>(
          `/api/v1/admin/staff/${encodeURIComponent(staffId)}/resend-invite`,
          { method: 'POST' },
        );
      },
      dashboard(windowDays = 30): Promise<DashboardStats> {
        return request<DashboardStats>(
          `/api/v1/admin/dashboard${buildQuery({ window_days: windowDays })}`,
        );
      },
      sessions(filters: SessionListFilters = {}): Promise<SessionListItem[]> {
        return request<SessionListItem[]>(`/api/v1/admin/sessions${buildQuery({ ...filters })}`);
      },
      /** Filterable analytics for the Reports screen (summary, sections, per-PM table). */
      report(filters: ReportFilters = {}): Promise<ReportOut> {
        return request<ReportOut>(`/api/v1/admin/reports${buildQuery({ ...filters })}`);
      },
      /**
       * Cross-survey analytics: every survey type compared, the response funnel
       * and the monthly trend. `report()` covers one survey at a time.
       */
      reportOverview(filters: OverviewFilters = {}): Promise<ReportOverviewOut> {
        return request<ReportOverviewOut>(
          `/api/v1/admin/reports/overview${buildQuery({ ...filters })}`,
        );
      },
      /** Fetches the filtered report CSV with auth header and returns a Blob. */
      async exportReportBlob(filters: ReportFilters = {}): Promise<Blob> {
        const token = config.getAuthToken?.() ?? null;
        const headers: Record<string, string> = { Accept: 'text/csv' };
        if (token) headers.Authorization = `Bearer ${token}`;
        const res = await fetch(
          `${root}/api/v1/admin/reports/export.csv${buildQuery({ ...filters })}`,
          { headers },
        );
        if (res.status === 401) config.onUnauthorized?.();
        if (!res.ok) {
          throw new ApiError(res.status, 'http_error', res.statusText);
        }
        return res.blob();
      },
      /** Aggregate tab counts — reflects the whole dataset, not the loaded page. */
      sessionCounts(filters: SessionListFilters = {}): Promise<SessionCounts> {
        return request<SessionCounts>(`/api/v1/admin/sessions/counts${buildQuery({ ...filters })}`);
      },
      sessionDetail(id: string): Promise<SessionDetailOut> {
        return request<SessionDetailOut>(`/api/v1/admin/sessions/${encodeURIComponent(id)}`);
      },
      resolveDetractor(id: string): Promise<SessionDetailOut> {
        return request<SessionDetailOut>(
          `/api/v1/admin/sessions/${encodeURIComponent(id)}/resolve`,
          { method: 'POST' },
        );
      },
      /** Soft-delete every response in a session; the session is re-classified and returned. */
      deleteSessionResponses(id: string): Promise<SessionDetailOut> {
        return request<SessionDetailOut>(
          `/api/v1/admin/sessions/${encodeURIComponent(id)}/responses`,
          { method: 'DELETE' },
        );
      },
      detractors(): Promise<DetractorItem[]> {
        return request<DetractorItem[]>('/api/v1/admin/detractors');
      },
      /** AI playground: mint a fresh flagged test session (non-prod only). */
      mintPlaygroundSession(payload: PlaygroundSessionIn): Promise<PlaygroundSession> {
        return request<PlaygroundSession>('/api/v1/admin/playground/sessions', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      },
      propertyManagers(): Promise<PropertyManagerOption[]> {
        return request<PropertyManagerOption[]>('/api/v1/admin/property-managers');
      },
      portfolios(): Promise<PortfolioManagerRow[]> {
        return request<PortfolioManagerRow[]>('/api/v1/admin/portfolios');
      },
      updatePortfolio(
        portfolioId: string,
        payload: PortfolioManagerUpdate,
      ): Promise<PortfolioManagerRow> {
        return request<PortfolioManagerRow>(
          `/api/v1/admin/portfolios/${encodeURIComponent(portfolioId)}`,
          { method: 'PUT', body: JSON.stringify(payload) },
        );
      },
      smsTemplates(): Promise<SmsTemplates> {
        return request<SmsTemplates>('/api/v1/admin/sms-templates');
      },
      updateSmsTemplate(surveyId: string, payload: SmsTemplateUpdate): Promise<SmsTemplate> {
        return request<SmsTemplate>(`/api/v1/admin/sms-templates/${encodeURIComponent(surveyId)}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      },
      // Surveys list + duplication (the admin Surveys screen)
      surveys(): Promise<SurveySummary[]> {
        return request<SurveySummary[]>('/api/v1/admin/surveys');
      },
      /** Copy a survey and its questions into a new, independent CUSTOM survey. */
      duplicateSurvey(surveyId: string, name?: string): Promise<SurveySummary> {
        return request<SurveySummary>(
          `/api/v1/admin/surveys/${encodeURIComponent(surveyId)}/duplicate`,
          { method: 'POST', body: JSON.stringify(name ? { name } : {}) },
        );
      },
      /** Rename a survey, or archive/restore a copy (seeded surveys can't be archived). */
      updateSurvey(surveyId: string, payload: SurveyUpdate): Promise<SurveySummary> {
        return request<SurveySummary>(`/api/v1/admin/surveys/${encodeURIComponent(surveyId)}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      },
      // Self-service survey question editor (TRD §15)
      surveyQuestions(surveyId: string): Promise<SurveyQuestionsOut> {
        return request<SurveyQuestionsOut>(
          `/api/v1/admin/surveys/${encodeURIComponent(surveyId)}/questions`,
        );
      },
      createSurveyQuestion(surveyId: string, payload: QuestionCreate): Promise<QuestionAdminOut> {
        return request<QuestionAdminOut>(
          `/api/v1/admin/surveys/${encodeURIComponent(surveyId)}/questions`,
          { method: 'POST', body: JSON.stringify(payload) },
        );
      },
      updateSurveyQuestion(
        surveyId: string,
        questionId: string,
        payload: QuestionUpdate,
      ): Promise<QuestionAdminOut> {
        return request<QuestionAdminOut>(
          `/api/v1/admin/surveys/${encodeURIComponent(surveyId)}/questions/${encodeURIComponent(questionId)}`,
          { method: 'PUT', body: JSON.stringify(payload) },
        );
      },
      deactivateSurveyQuestion(surveyId: string, questionId: string): Promise<QuestionAdminOut> {
        return request<QuestionAdminOut>(
          `/api/v1/admin/surveys/${encodeURIComponent(surveyId)}/questions/${encodeURIComponent(questionId)}`,
          { method: 'DELETE' },
        );
      },
      reorderSurveyQuestions(surveyId: string, questionIds: string[]): Promise<SurveyQuestionsOut> {
        return request<SurveyQuestionsOut>(
          `/api/v1/admin/surveys/${encodeURIComponent(surveyId)}/questions/order`,
          { method: 'PUT', body: JSON.stringify({ question_ids: questionIds }) },
        );
      },
      reminderSettings(): Promise<ReminderSettings> {
        return request<ReminderSettings>('/api/v1/admin/settings/reminder');
      },
      updateReminderSettings(reminderSendTimeLocal: string): Promise<ReminderSettings> {
        return request<ReminderSettings>('/api/v1/admin/settings/reminder', {
          method: 'PUT',
          body: JSON.stringify({ reminder_send_time_local: reminderSendTimeLocal }),
        });
      },
      /** Active contacts; pass `{ archived: true }` for the archived ones instead. */
      contacts(role?: CustomerRole, opts: { archived?: boolean } = {}): Promise<ContactOut[]> {
        return request<ContactOut[]>(
          `/api/v1/admin/contacts${buildQuery({ role, archived: opts.archived ? 'true' : undefined })}`,
        );
      },
      /** Single contact — backs the Contact detail page. */
      contact(customerId: string): Promise<ContactOut> {
        return request<ContactOut>(`/api/v1/admin/contacts/${encodeURIComponent(customerId)}`);
      },
      /** One (owner, property) row per ownership — the compose recipient picker list. */
      surveyTargets(): Promise<OwnershipTargetOut[]> {
        return request<OwnershipTargetOut[]>('/api/v1/admin/survey-targets');
      },
      /** Properties with their PM and active owner(s) — the Properties page. */
      properties(): Promise<PropertyWithOwnersOut[]> {
        return request<PropertyWithOwnersOut[]>('/api/v1/admin/properties');
      },
      /** Single property with its active owners — backs the Property detail page. */
      property(propertyId: string): Promise<PropertyWithOwnersOut> {
        return request<PropertyWithOwnersOut>(
          `/api/v1/admin/properties/${encodeURIComponent(propertyId)}`,
        );
      },
      createContact(payload: ContactCreate): Promise<ContactOut> {
        return request<ContactOut>('/api/v1/admin/contacts', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      },
      updateContact(customerId: string, payload: ContactUpdate): Promise<ContactOut> {
        return request<ContactOut>(`/api/v1/admin/contacts/${encodeURIComponent(customerId)}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      },
      /** Partial edit of a contact's greeting name (fix odd owner names in drip SMS). */
      updateContactGreeting(customerId: string, payload: GreetingUpdate): Promise<ContactOut> {
        return request<ContactOut>(
          `/api/v1/admin/contacts/${encodeURIComponent(customerId)}/greeting`,
          { method: 'PATCH', body: JSON.stringify(payload) },
        );
      },
      /** Flip a contact's opt-out flag on its own, without a full-replace PUT. */
      updateContactOptOut(customerId: string, payload: OptOutUpdate): Promise<ContactOut> {
        return request<ContactOut>(
          `/api/v1/admin/contacts/${encodeURIComponent(customerId)}/opt-out`,
          { method: 'PATCH', body: JSON.stringify(payload) },
        );
      },
      /**
       * Flip a contact's non-prod SMS sandbox flag. Outside prod only a flagged contact
       * can receive a real SMS, so this is the one control over who staging may text.
       */
      updateContactTestFlag(customerId: string, isTestContact: boolean): Promise<ContactOut> {
        return request<ContactOut>(
          `/api/v1/admin/contacts/${encodeURIComponent(customerId)}/test-contact`,
          { method: 'PATCH', body: JSON.stringify({ is_test_contact: isTestContact }) },
        );
      },
      /**
       * Archive (hide from lists, stop all messages, keep survey history) or restore a
       * contact. The everyday alternative to delete, which is admin-only.
       */
      setContactArchived(customerId: string, archived: boolean): Promise<ContactOut> {
        return request<ContactOut>(
          `/api/v1/admin/contacts/${encodeURIComponent(customerId)}/archived`,
          { method: 'PATCH', body: JSON.stringify({ archived }) },
        );
      },
      deleteContact(customerId: string): Promise<void> {
        return request<void>(`/api/v1/admin/contacts/${encodeURIComponent(customerId)}`, {
          method: 'DELETE',
        });
      },
      /**
       * Send one survey to one contact. Omit `surveyId` for the General Survey
       * (back-compat); pass `propertyId` to target a non-primary property (e.g.
       * sending from a Property detail page for a multi-property owner).
       */
      sendSurvey(
        customerId: string,
        surveyId?: string,
        propertyId?: string,
        asConversation?: boolean,
      ): Promise<SendSurveyOut> {
        return request<SendSurveyOut>(
          `/api/v1/admin/contacts/${encodeURIComponent(customerId)}/send-survey`,
          {
            method: 'POST',
            body: JSON.stringify({
              survey_id: surveyId,
              property_id: propertyId,
              // Undefined, not false: omitting it means "follow the survey's default",
              // while false actively forces the link.
              as_conversation: asConversation,
            }),
          },
        );
      },
      /** Omit `surveyId` for the General Survey; pass one to run a duplicated survey. */
      sendSurveyBatch(
        targets: SendTarget[],
        surveyId?: string,
        asConversation?: boolean,
      ): Promise<BatchSendOut> {
        return request<BatchSendOut>('/api/v1/admin/surveys/send', {
          method: 'POST',
          body: JSON.stringify({
            targets,
            ...(surveyId ? { survey_id: surveyId } : {}),
            ...(asConversation === undefined ? {} : { as_conversation: asConversation }),
          }),
        });
      },
      /**
       * Triggered surveys (Phase 2): PM events eligible for a manual send. Defaults to
       * unsent events from the last 30 days; pass an event type to filter,
       * includeProcessed to show sent ones, includeOlder to lift the age window (lease
       * renewals never auto-send, so their backlog goes back months).
       */
      pmEvents(
        eventType?: PmEventType,
        includeProcessed = false,
        includeOlder = false,
      ): Promise<PmEventTarget[]> {
        return request<PmEventTarget[]>(
          `/api/v1/admin/pm-events${buildQuery({
            event_type: eventType,
            include_processed: includeProcessed ? 'true' : undefined,
            include_older: includeOlder ? 'true' : undefined,
          })}`,
        );
      },
      /** The automated send schedule for the triggered-survey tabs. */
      triggeredAutosend(): Promise<TriggeredAutosend> {
        return request<TriggeredAutosend>('/api/v1/admin/pm-events/autosend');
      },
      /** Change the daily send time / which event types automate. Admin role required. */
      updateTriggeredAutosend(payload: TriggeredAutosendUpdate): Promise<TriggeredAutosend> {
        return request<TriggeredAutosend>('/api/v1/admin/pm-events/autosend', {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      },
      /** Manually send the triggered survey for one PM event. */
      sendPmEventSurvey(pmEventId: string): Promise<SendSurveyOut> {
        return request<SendSurveyOut>(
          `/api/v1/admin/pm-events/${encodeURIComponent(pmEventId)}/send`,
          { method: 'POST' },
        );
      },
      /** Manually send triggered surveys for many PM events (skips opted-out / already-sent). */
      sendPmEventSurveys(pmEventIds: string[]): Promise<BatchSendOut> {
        return request<BatchSendOut>('/api/v1/admin/pm-events/send', {
          method: 'POST',
          body: JSON.stringify({ pm_event_ids: pmEventIds }),
        });
      },
      /** Resolves each (owner, property) target's SMS (placeholders filled) without sending. */
      previewSurveyBatch(targets: SendTarget[], surveyId?: string): Promise<SmsPreviewOut> {
        return request<SmsPreviewOut>('/api/v1/admin/surveys/preview', {
          method: 'POST',
          body: JSON.stringify(surveyId ? { targets, survey_id: surveyId } : { targets }),
        });
      },
      /**
       * Like previewSurveyBatch but for (owner, property) targets and a specific survey —
       * used to preview triggered-survey copy (e.g. routine inspection) before sending.
       */
      previewSurveyTargets(
        targets: { customer_id: string; property_id: string | null }[],
        surveyId: string,
      ): Promise<SmsPreviewOut> {
        return request<SmsPreviewOut>('/api/v1/admin/surveys/preview', {
          method: 'POST',
          body: JSON.stringify({ targets, survey_id: surveyId }),
        });
      },
      /**
       * Queue a General Survey batch for a future send. `scheduledFor` is a
       * wall-clock time; with no offset it's interpreted in the app timezone
       * (Australia/Brisbane) and must be in the future.
       */
      scheduleSurveyBatch(customerIds: string[], scheduledFor: string): Promise<ScheduledBatchOut> {
        return request<ScheduledBatchOut>('/api/v1/admin/surveys/schedule', {
          method: 'POST',
          body: JSON.stringify({ customer_ids: customerIds, scheduled_for: scheduledFor }),
        });
      },
      /** Lists scheduled batches (newest fire-time first). */
      scheduledSurveys(): Promise<ScheduledBatchOut[]> {
        return request<ScheduledBatchOut[]>('/api/v1/admin/surveys/scheduled');
      },
      /** Cancels a still-pending scheduled batch and deletes its Cloud Task. */
      cancelScheduledSurvey(batchId: string): Promise<ScheduledBatchOut> {
        return request<ScheduledBatchOut>(
          `/api/v1/admin/surveys/scheduled/${encodeURIComponent(batchId)}/cancel`,
          { method: 'POST' },
        );
      },
      /** Size of the contactable, not-recently-surveyed owner pool (drip estimate). */
      eligibleOwnerCount(): Promise<EligibleCountOut> {
        return request<EligibleCountOut>('/api/v1/admin/surveys/eligible-count');
      },
      /** Start an automated daily drip — samples the eligible pool, no manual recipients. */
      createDripCampaign(payload: DripCampaignIn): Promise<DripCampaignOut> {
        return request<DripCampaignOut>('/api/v1/admin/surveys/drip', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      },
      /** Lists drip campaigns (newest first). */
      dripCampaigns(): Promise<DripCampaignOut[]> {
        return request<DripCampaignOut[]>('/api/v1/admin/surveys/drip');
      },
      /** Edits the running drip's schedule and re-enqueues its next daily Cloud Task. */
      updateDripCampaign(campaignId: string, payload: DripCampaignIn): Promise<DripCampaignOut> {
        return request<DripCampaignOut>(
          `/api/v1/admin/surveys/drip/${encodeURIComponent(campaignId)}`,
          { method: 'PUT', body: JSON.stringify(payload) },
        );
      },
      /** Stops an ACTIVE drip and deletes its pending daily Cloud Task. */
      cancelDripCampaign(campaignId: string): Promise<DripCampaignOut> {
        return request<DripCampaignOut>(
          `/api/v1/admin/surveys/drip/${encodeURIComponent(campaignId)}/cancel`,
          { method: 'POST' },
        );
      },
      /** Projected per-day send counts for a drip (feeds the schedule panel's date dropdown). */
      dripSchedule(campaignId: string): Promise<DripScheduleOut> {
        return request<DripScheduleOut>(
          `/api/v1/admin/surveys/drip/${encodeURIComponent(campaignId)}/schedule`,
        );
      },
      /** Contacts projected to be sent on `date` ("YYYY-MM-DD"), each with their resolved SMS. */
      dripRecipients(campaignId: string, date: string): Promise<DripRecipientOut[]> {
        return request<DripRecipientOut[]>(
          `/api/v1/admin/surveys/drip/${encodeURIComponent(campaignId)}/recipients${buildQuery({
            date,
          })}`,
        );
      },
      /** Returns the URL — the FE triggers a download by anchor+click. */
      exportCsvUrl(filters: SessionListFilters = {}): string {
        return `${root}/api/v1/admin/sessions/export.csv${buildQuery({ ...filters })}`;
      },
      /** Fetches CSV with auth header and returns a Blob (for in-page download). */
      async exportCsvBlob(filters: SessionListFilters = {}): Promise<Blob> {
        const token = config.getAuthToken?.() ?? null;
        const headers: Record<string, string> = { Accept: 'text/csv' };
        if (token) headers.Authorization = `Bearer ${token}`;
        const res = await fetch(
          `${root}/api/v1/admin/sessions/export.csv${buildQuery({ ...filters })}`,
          { headers },
        );
        if (res.status === 401) config.onUnauthorized?.();
        if (!res.ok) {
          throw new ApiError(res.status, 'http_error', res.statusText);
        }
        return res.blob();
      },
    },

    /** Dev-only: present in dev/test backends; 404s in prod. */
    dev: {
      mintSession(): Promise<{ session_id: string; token: string; link: string }> {
        return request('/api/v1/dev/sessions', {
          method: 'POST',
          body: JSON.stringify({}),
        });
      },
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;

/** Retained for back-compat with the pre-wired placeholder export. */
export type ApiClientStatus = 'unconfigured' | 'configured';
export const apiClient = {
  status: 'configured' as ApiClientStatus,
};

// `paths` is re-exported above for consumers that want to bypass the helper
// and type fetch requests directly against the OpenAPI spec.
export type _Paths = paths;
